import React, { useEffect } from 'react';

import Icon from '../ui-components/Icon';
import Divider from '../ui-components/Divider';
import { selectMicIsMuted } from 'flow/selectors/mute-selectors';
import { twMerge } from 'tailwind-merge';
import IconButton from '../ui-components/IconButton';
import useAppContext from 'flow/hooks/useAppContext';
import { getHoverTextShortcutMic } from 'flow/utils/call-utils';
import { addUserPhoneLogEntry } from 'flow/utils/browser-utils';
import DialPadButton from 'flow/components/communication-canvas/buttons/DialPadButton';
import {
    mutePhone,
    sendDTMF,
    toggleFocusedCall,
} from 'flow/actions/phone-actions';
import {
    getNumLocalCalls,
    isLocalFocusedCallSessionEstablished,
    allowToggleCall,
    isCoListening,
    selectLoggedInUserIsInCall,
} from 'flow/selectors/call-selectors';
import { useAppDispatch, useAppSelector } from 'flow/hooks/useRedux';
import { isCompanionSignallingSurface } from 'flow/companion/companion-utils';

interface Props {
    onNewCall: () => void;
}

const CallFooter = ({ onNewCall }: Props) => {
    const dispatch = useAppDispatch();
    const { i18next } = useAppContext();

    const isMuted = useAppSelector(selectMicIsMuted);
    const numLocalCalls = useAppSelector(getNumLocalCalls);
    const isCoListeningSession = useAppSelector(isCoListening);
    const canToggleCalls = useAppSelector(allowToggleCall);
    const localSessionEstablished = useAppSelector(
        isLocalFocusedCallSessionEstablished,
    );
    // Model A: in companion mode the call is on the companion, so there is no local session and
    // no local call - both gates below would hide mute and DTMF entirely. selectLoggedInUserIsInCall
    // is built on getFocusedActiveCall, which falls back to the first active call when there is no
    // local one, so it sees the companion-owned call. The controls themselves are relayed (see
    // mutePhone/sendDTMF).
    const isInCall = useAppSelector(selectLoggedInUserIsInCall);
    const companionSurface = useAppSelector(isCompanionSignallingSurface);
    const companionCallInProgress = companionSurface && isInCall;

    const canSendDTMF =
        (localSessionEstablished || companionCallInProgress) &&
        !isCoListeningSession;

    const canMuteUnmute =
        (numLocalCalls > 0 || companionCallInProgress) && !isCoListeningSession;

    const onKeyDown = (event: KeyboardEvent) => {
        const superKey = event.ctrlKey || event.metaKey;
        if ((event.key === 'D' || event.key === 'd') && superKey) {
            event.preventDefault();
            addUserPhoneLogEntry(
                `Comm canvas -> ${isMuted ? 'unmute' : 'mute'} clicked`,
            );
            dispatch(mutePhone(!isMuted));
        }
    };

    useEffect(() => {
        document.addEventListener('keydown', onKeyDown);
        return () => {
            document.removeEventListener('keydown', onKeyDown);
        };
    });

    return (
        <>
            <Divider className="mb-0 text-gray-300" />
            <div className="flex items-center justify-evenly p-3">
                {canMuteUnmute ? (
                    <IconButton
                        className={twMerge(
                            'flex size-10 grow-0 text-white',
                            isMuted
                                ? 'hover:darken bg-red'
                                : 'bg-black hover:bg-gray-600',
                        )}
                        onClick={() => {
                            addUserPhoneLogEntry(
                                `Comm canvas -> ${
                                    isMuted ? 'unmute' : 'mute'
                                } clicked`,
                            );
                            dispatch(mutePhone(!isMuted));
                        }}
                        data-test-id={'mute-button'}
                        tooltipLabel={
                            isMuted
                                ? i18next.t('Call.Canvas.Title.Unmute') +
                                  getHoverTextShortcutMic()
                                : i18next.t('Call.Canvas.Title.Mute') +
                                  getHoverTextShortcutMic()
                        }
                        tooltipPlacement="top"
                    >
                        <Icon iconKey={isMuted ? 'mic_off' : 'mic'} />
                    </IconButton>
                ) : null}

                {canSendDTMF ? (
                    <DialPadButton
                        className="flex size-10 grow-0 bg-black text-white hover:bg-gray-600"
                        onClick={(symbol: string) => {
                            addUserPhoneLogEntry(
                                'Comm canvas -> DTMF symbol clicked',
                            );
                            dispatch(sendDTMF(symbol));
                        }}
                    />
                ) : null}

                {onNewCall ? (
                    <IconButton
                        className="flex size-10 grow-0 bg-black text-white hover:bg-gray-600"
                        onClick={onNewCall}
                        data-test-id={'add-call-button'}
                        tooltipLabel={i18next.t('call.new')}
                        tooltipPlacement="top"
                    >
                        <Icon iconKey="addCallIcon" />
                    </IconButton>
                ) : null}

                {canToggleCalls ? (
                    <IconButton
                        onClick={() => {
                            addUserPhoneLogEntry(
                                'Comm canvas -> toggle call clicked',
                            );
                            dispatch(toggleFocusedCall);
                        }}
                        className="flex size-10 grow-0 bg-black text-white hover:bg-gray-600"
                        data-test-id={'toggle-button'}
                        tooltipLabel={i18next.t('Call.Canvas.Title.Toggle')}
                        tooltipPlacement="top"
                    >
                        <Icon iconKey="loop" />
                    </IconButton>
                ) : null}
            </div>
        </>
    );
};

export default React.memo(CallFooter);
